/**
 * Zone 4: Recommended Actions
 *
 * The most important part — what the operator should do next.
 * 2–4 actions max, prioritized, minimal cognitive load.
 */

import type { RecommendedAction } from '../../services/investigationEngine';

interface RecommendedActionsProps {
  actions: readonly RecommendedAction[];
  onAction: (actionType: string) => void;
  disabled?: boolean;
}

const VARIANT_BASE: Record<string, string> = {
  primary: 'bg-emerald-600 text-white',
  secondary: 'bg-slate-700 text-slate-200',
  danger: 'bg-rose-600 text-white',
};

const VARIANT_HOVER: Record<string, string> = {
  primary: 'hover:bg-emerald-500',
  secondary: 'hover:bg-slate-600',
  danger: 'hover:bg-rose-500',
};

export function RecommendedActions({ actions, onAction, disabled = false }: RecommendedActionsProps) {
  if (actions.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg px-5 py-6 text-center">
        <p className="text-sm text-slate-500">No actions needed</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-700/50">
        <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
          Recommended actions
        </h3>
      </div>

      <div className="p-4 space-y-2">
        {actions.map((action) => {
          const base = VARIANT_BASE[action.variant] ?? VARIANT_BASE.secondary!;
          const hover = disabled ? '' : (VARIANT_HOVER[action.variant] ?? VARIANT_HOVER.secondary!);

          return (
            <button
              key={action.id}
              type="button"
              disabled={disabled}
              onClick={() => onAction(action.actionType)}
              className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${base} ${hover} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className="text-sm font-medium block">{action.label}</span>
              <span className="text-[10px] opacity-75 block mt-0.5">{action.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
